import { Form, Formik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';

import { EMPTY_ID_ARRAY } from '@e-socna/empty-client';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { Id, SectionTypeId } from '@e-socna/data-layer';

import { ConfirmBeforeExitForm } from '../../components/ConfirmBeforeExitForm';
import { FORM_LAYOUT_STYLE, FormLayout } from '../../components/FormLayout';
import { Loading } from '../../components/Loading';
import { NoData } from '../../components/Illustrations/NoData';
import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';
import { DataGrid } from '../../components/DataGrid';

import { FormVariant } from '../enums/variant.enum';

import { columns } from './columns';
import { useFindAllCompatibleSectionTemplatesQuery } from './hooks/useFindAllCompatibleSectionTemplatesQuery.hook';
import { CompatibleSectionTemplatesSchema } from './schemas/compatible-section-templates.schema';
import { useCompatibleSectionTemplatesSubmit } from './hooks/useSubmit.hook';
import type { SubmitHandler } from './types/submit-handler.type';
import type { CompatibleSectionTemplatesFormValues } from './types/compatible-section-templates-form-values.type';

interface Props {
  readonly alreadyUsedIds?: readonly Id[];
  readonly formVariant?: FormVariant;
  readonly initialValues?: CompatibleSectionTemplatesFormValues;
  readonly onCancel: () => void;
  readonly onSubmit: SubmitHandler;
  readonly onlyOneChoice?: boolean;
  readonly setMustBlockAction: (value: boolean) => void;
  readonly typeId?: SectionTypeId;
}

export const CompatibleSectionTemplatesForm = ({
  alreadyUsedIds = EMPTY_ID_ARRAY,
  formVariant,
  initialValues,
  onCancel: handleCancel,
  onSubmit: pHandleSubmit,
  onlyOneChoice = false,
  setMustBlockAction,
  typeId,
}: Readonly<Props>) => {
  // ui state
  const { ui } = useUiContext();

  const { data, isSuccess } = useFindAllCompatibleSectionTemplatesQuery({
    alreadyUsedIds,
    typeId,
  });

  const compatibleSectionTemplates =
    data?.findAllEnabledAndCompatibleSectionTemplates;

  const { handleSubmit } = useCompatibleSectionTemplatesSubmit({
    handleSubmit: pHandleSubmit,
    compatibleSectionTemplates,
  });

  if (!isSuccess) {
    return <Loading />;
  }

  if (compatibleSectionTemplates?.length) {
    return (
      <Formik
        enableReinitialize
        initialValues={{
          sectionTemplateIdsToInsert: EMPTY_ARRAY,
          ...initialValues,
        }}
        onSubmit={handleSubmit}
        validationSchema={CompatibleSectionTemplatesSchema}
      >
        {({
          values,
          errors,
          touched,
          handleSubmit: formikSubmit,
          isSubmitting,
          setFieldValue,
          isValid,
        }) => (
          // eslint-disable-next-line react/forbid-component-props
          <Form onSubmit={formikSubmit} style={FORM_LAYOUT_STYLE}>
            <ConfirmBeforeExitForm setMustBlockAction={setMustBlockAction} />
            <FormLayout
              actions={
                <Stack direction="row" spacing={2}>
                  <Button
                    disabled={!isValid}
                    loading={isSubmitting || ui.isBackgroundOperationRunning}
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    {!formVariant || formVariant === FormVariant.Creation
                      ? 'Insérer'
                      : 'Modifier'}
                  </Button>
                  <Button
                    onClick={handleCancel}
                    size="large"
                    variant="outlined"
                  >
                    Annuler
                  </Button>
                </Stack>
              }
              error={
                !!errors.sectionTemplateIdsToInsert &&
                !!touched.sectionTemplateIdsToInsert && (
                  <Alert severity="error">
                    {getIn(errors, 'sectionTemplateIdsToInsert')}
                  </Alert>
                )
              }
              title="Insérer des Modèles de Section"
            >
              <DataGrid
                checkboxSelection
                columns={columns}
                loading={!compatibleSectionTemplates.length}
                // eslint-disable-next-line react/jsx-no-bind
                onRowSelectionModelChange={(newSelection) => {
                  void setFieldValue(
                    'sectionTemplateIdsToInsert',
                    !onlyOneChoice
                      ? [...newSelection.ids]
                      : [[...newSelection.ids].pop()],
                  );
                }}
                rowSelectionModel={{
                  type: 'include',
                  ids: new Set(values.sectionTemplateIdsToInsert),
                }}
                rows={compatibleSectionTemplates}
                sx={{ mb: 1 }}
              />
            </FormLayout>
          </Form>
        )}
      </Formik>
    );
  }

  return (
    <Box sx={{ pt: 8, width: '100%' }}>
      <NoData />
    </Box>
  );
};
